import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { SiteFooter, FinalCTA } from "@/components/site/FinalCTA";
import { api } from "@/lib/api";
import { PAGES } from "@/constants/testIds";

const INTRO_PARAGRAPHS = [
  "India is one of the world's oldest continuous civilizations, with a cultural heritage spanning more than 5,000 years. From the ancient cities of the Indus Valley to today's vibrant metropolitan centres, India's identity has been shaped by countless generations of families who have preserved traditions, languages, arts, beliefs and values.",
  "Indian culture is not confined to monuments or museums — it lives in family homes, village festivals, handwritten recipes, oral histories, sacred rituals and the everyday customs passed from grandparents to grandchildren. Every family, whether rooted in a remote Himalayan village, a coastal fishing town or a bustling city, contributes a unique chapter to the story of India.",
  "At IndianFamilyTree.com, we believe that understanding your family history is also a journey into India's rich cultural and historical landscape. By preserving family stories alongside the places, communities and traditions from which they emerged, we help connect generations with the heritage that shaped their lives.",
];

const CLOSING = [
  "The story of India is ultimately the story of its families. Every surname, ancestral home, recipe, song, festival and photograph adds another thread to the nation's cultural fabric. By preserving these memories today, we ensure that future generations inherit not only names and dates but also the traditions, values and experiences that give those names meaning.",
  "Whether your roots lie in a Himalayan village, a coastal town in Kerala, the deserts of Rajasthan, the tea gardens of Assam, or an Indian community overseas, your family's journey is part of India's enduring heritage.",
];

function SectionBlock({ section }) {
  return (
    <motion.section
      id={section.id}
      data-testid={PAGES.heritageSection(section.id)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="border-b border-ivory-300 py-16 md:py-24"
    >
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">{section.kicker}</span>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight text-ink leading-[0.95]">
              {section.title}
            </h2>
            <p className="mt-5 text-ink-50 text-base md:text-lg font-light leading-relaxed max-w-md">
              {section.intro}
            </p>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-7">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {section.items.map((it, i) => (
              <li key={it.slug} className="border-b border-ivory-200">
                <Link
                  to={`/indian-heritage/${it.slug}`}
                  data-testid={PAGES.heritageItem(it.slug)}
                  className="group flex items-center justify-between gap-4 py-4"
                >
                  <span className="flex items-baseline gap-3">
                    <span className="font-mono text-[10px] tracking-[0.24em] text-ink-50 group-hover:text-saffron transition">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-lg md:text-xl text-ink group-hover:text-saffron transition">
                      {it.label}
                    </span>
                  </span>
                  <span className="text-ink-50 group-hover:text-saffron transition text-lg">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.section>
  );
}

export default function IndianHeritagePage() {
  const [sections, setSections] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/heritage/article").then((r) => setSections(r.data.sections || [])).catch(() => {});
  }, []);

  const toc = useMemo(() => sections.map((s) => ({ id: s.id, title: s.title })), [sections]);

  const q = query.trim().toLowerCase();
  const flatMatches = useMemo(() => {
    if (!q) return [];
    const out = [];
    for (const s of sections) {
      for (const it of s.items || []) {
        if (
          it.label.toLowerCase().includes(q) ||
          s.title.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q)
        ) {
          out.push({ ...it, section_title: s.title, section_id: s.id });
        }
      }
    }
    return out.slice(0, 24);
  }, [q, sections]);

  return (
    <div data-testid={PAGES.heritage} className="min-h-screen bg-ivory text-ink">
      <PageHeader
        eyebrow="Indian Heritage · A living legacy"
        title="Indian culture and"
        italic="heritage"
        tail="— across generations."
        subtitle="Preserving the stories, traditions and values that define India. A SocialPulse heritage essay — with a page of its own for every festival, dance, cuisine, craft, faith, language and family value below. Tap any item to read a fuller note, written for the archive."
      >
        {/* Search box */}
        <div className="mt-10 max-w-xl">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-50" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Diwali, Bharatanatyam, Chikankari, Kerala…"
              data-testid="heritage-search-input"
              className="w-full h-14 pl-11 pr-11 rounded-full border border-ivory-300 bg-ivory focus:border-tulsi focus:outline-none font-serif text-lg placeholder:text-ink-50/50"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-ink-50 hover:text-ink hover:bg-ivory-200 transition"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <AnimatePresence>
            {q && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                data-testid="heritage-search-results"
                className="mt-3 rounded-2xl border border-ivory-300 bg-ivory overflow-hidden shadow-[0_20px_60px_-30px_rgba(10,15,13,0.25)]"
              >
                {flatMatches.length === 0 ? (
                  <div className="p-6 text-ink-50 text-sm">
                    Nothing found for “{query}” — try a festival, dance or cuisine.
                  </div>
                ) : (
                  <ul className="max-h-[380px] overflow-y-auto divide-y divide-ivory-200">
                    {flatMatches.map((m) => (
                      <li key={m.slug}>
                        <Link
                          to={`/indian-heritage/${m.slug}`}
                          data-testid={`heritage-search-result-${m.slug}`}
                          className="group flex items-center justify-between gap-4 px-5 py-3.5 hover:bg-ivory-100 transition"
                        >
                          <span className="flex flex-col">
                            <span className="font-serif text-lg text-ink group-hover:text-saffron transition">
                              {m.label}
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.24em] text-ink-50 mt-0.5">
                              {m.section_title}
                            </span>
                          </span>
                          <span className="text-ink-50 group-hover:text-saffron transition">→</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </PageHeader>

      {/* Introduction */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <span className="text-xs uppercase tracking-[0.3em] text-saffron">On this page</span>
              <ol className="mt-4 space-y-2">
                {toc.map((t, i) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      className="group flex items-baseline gap-3 py-1.5 text-ink-50 hover:text-ink transition"
                    >
                      <span className="font-mono text-[10px] tracking-[0.24em]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-serif text-lg leading-tight group-hover:text-saffron transition">
                        {t.title}
                      </span>
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-8 lg:pl-8">
            <div className="space-y-6 max-w-2xl">
              {INTRO_PARAGRAPHS.map((p, i) => (
                <p key={i} className="text-lg md:text-xl text-ink font-light leading-relaxed first-letter:font-serif first-letter:text-5xl first-letter:mr-2 first-letter:float-left first-letter:leading-[0.85] first-letter:text-saffron">
                  {p}
                </p>
              ))}
            </div>

            {/* A civilization, not just a nation */}
            <div className="mt-16 border-t border-ivory-300 pt-10 max-w-2xl">
              <span className="text-xs uppercase tracking-[0.3em] text-saffron">A civilisation</span>
              <h3 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
                Not just a nation.
              </h3>
              <p className="mt-5 text-ink-50 text-base md:text-lg font-light leading-relaxed">
                India is often described as a civilisation rather than simply a modern nation-state.
                Its cultural foundations stretch back to the Indus Valley Civilization (c. 3300–1300 BCE),
                one of the earliest urban societies in the world. Archaeological sites such as Harappa
                and Mohenjo-daro reveal sophisticated city planning, trade networks, craftsmanship and
                social organisation.
              </p>
              <p className="mt-4 text-ink-50 text-base md:text-lg font-light leading-relaxed">
                Over thousands of years, India witnessed the rise and interaction of numerous kingdoms
                and empires — the Mauryas, Guptas, Cholas, Pallavas, Chalukyas, Mughals, Marathas, Ahoms,
                Vijayanagara, the Sikh empire and many regional dynasties. Rather than replacing one another,
                many traditions blended and evolved, creating a society where ancient customs continue
                alongside modern innovation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* All ten sections with links */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {sections.map((s) => (
          <SectionBlock key={s.id} section={s} />
        ))}
      </div>

      {/* Diaspora + genealogy + why preserve */}
      <section className="relative py-24 bg-tulsi text-ivory">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-6">
            <span className="text-xs uppercase tracking-[0.3em] text-marigold">The diaspora</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-[0.95]">
              Thirty million,
              <span className="italic text-marigold"> home </span>
              carried in a suitcase.
            </h2>
            <p className="mt-6 text-ivory/70 text-lg font-light max-w-md leading-relaxed">
              More than 30 million people of Indian origin live outside India today, forming one of the
              world's largest diaspora communities — from Toronto to Trinidad, Sydney to Suva. Despite
              geographic distance, many continue to preserve languages, festivals, cuisine and family
              traditions while contributing to their adopted countries.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["United States","Canada","United Kingdom","Australia","New Zealand","South Africa","Fiji","Mauritius","Malaysia","Singapore","United Arab Emirates","Trinidad and Tobago","Guyana","Suriname"].map((c) => (
                <span key={c} className="text-xs px-3 py-1.5 rounded-full border border-ivory/25 text-ivory/80">{c}</span>
              ))}
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 md:pl-16">
            <span className="text-xs uppercase tracking-[0.3em] text-marigold">Genealogy in India</span>
            <h3 className="mt-4 font-serif text-3xl md:text-4xl tracking-tight leading-tight">
              Kept in temples, panjis, land records and elders' memories.
            </h3>
            <p className="mt-5 text-ivory/70 text-base md:text-lg font-light leading-relaxed">
              India has practised genealogy for centuries — through temple registers at Haridwar, Puri
              and Varanasi; through community genealogists (pandas and bhats); through land and revenue
              records, family manuscripts, clan histories, village archives and colonial censuses.
              Technology now lets us preserve them digitally, and share them securely with relatives
              around the world.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-ivory/70">
              {["Temple genealogy registers","Community genealogists","Land and revenue records","Family manuscripts","Clan histories","Village archives","Court documents","Colonial censuses","Family photographs","Oral traditions"].map((s) => (
                <li key={s}>· {s}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="relative py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">A legacy for future generations</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink leading-tight">
              Why preserving family heritage matters.
            </h2>
            <div className="mt-6 space-y-5 text-ink-50 text-lg font-light leading-relaxed">
              {CLOSING.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        </div>
      </section>

      <FinalCTA onStart={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
      <SiteFooter />
    </div>
  );
}
